import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Align, drawMultilineText, drawText } from '../render/drawText';
import styled from 'styled-components';
import { AdjustableText } from './progressModel';
import { useThrottle } from '../util/useThrottle';

export enum CertificateType {
  GREEN = "green",
  BRONZE = "bronze",
  SILVER = "silver",
  GOLD = "gold"
}

/*
export interface Check {
  checked: boolean;
  text: string;
  checkKey?: string;
}
  */

export class Check {
  plainText: string;
  adjustableText: AdjustableText;
  checked: boolean;
  checkKey?: string;
  maxLineCount: number;
  constructor(checked: boolean, text: string | AdjustableText, checkKey?: string, maxLineCount: number = 2) {
    this.checked = checked;
    if (typeof text === 'string') {
      this.plainText = text;
    } else {
      this.adjustableText = text;
    }
    this.checkKey = checkKey;
    this.maxLineCount = maxLineCount;
  }

  get text() {
    return this.plainText || this.adjustableText.text
  }
}

type Props = {
  forwardCanvasRef?: RefObject<HTMLCanvasElement>;
  type: CertificateType; 
  width: number;
  height: number;
  scale: number;
  checks: Check[];
  header: string;
  subheader: string;
  registerBlobGetter?: (getter: () => Promise<Blob>) => void;
  onCheckClick?: (checkKey) => void;
};

const drawCheckbox = (
  mctx: CanvasRenderingContext2D,
  sctx: CanvasRenderingContext2D,
  fontSize: number,
  checked: boolean,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  hue: number,
  saturation: number,
  lightness: number,
  isDryRun: boolean,
): [DOMRect, number] => {
  mctx.save();
  mctx.strokeStyle = `hsl(${hue}, ${saturation}%, ${Math.round(lightness)}%)`;
  mctx.lineWidth = 3;
  mctx.globalAlpha = isDryRun ? 0 : 1;
  mctx.strokeRect(x, y, 30, 30);
  const [bb, lineCount] = drawMultilineText(mctx, fontSize, text, y, hue, saturation, lightness, false, false, x + 50, maxWidth - 50, Align.LEFT, 1.3);
  if (checked && !isDryRun) {
    sctx.strokeStyle = `hsl(${hue}, ${saturation}%, ${lightness + 18}%)`; // 'hsl(37, 91%, 45%)';
    sctx.lineWidth = 5;
    sctx.beginPath();
    sctx.moveTo(x + 30, y + 2);
    sctx.lineTo(x + 12, y + 20);
    sctx.lineTo(x + 4, y + 12);
    sctx.stroke();
    sctx.strokeStyle = `hsl(${hue}, ${saturation}%, ${Math.round(1 * lightness / 3)}%)`; // 'hsl(37, 91%, 9%)';
    sctx.lineWidth = 5;
    sctx.beginPath();
    sctx.moveTo(x + 30, y + 4);
    sctx.lineTo(x + 12, y + 22);
    sctx.lineTo(x + 4, y + 14);
    sctx.stroke();
    sctx.strokeStyle = `hsl(${hue}, ${saturation}%, ${Math.round(2 * lightness / 3)}%)`; // 'hsl(37, 91%, 18%)';
    sctx.lineWidth = 5;
    sctx.beginPath();
    sctx.moveTo(x + 30, y + 3);
    sctx.lineTo(x + 12, y + 21);
    sctx.lineTo(x + 4, y + 13);
    sctx.stroke();
  }
  const xOff = bb.x - x;
  bb.x -= xOff;
  bb.width += xOff;
  mctx.restore();
  return [bb, lineCount];
}

export const Certificate: React.FC<Props> = ({ 
  type, width, height, scale, checks, registerBlobGetter,
  header, subheader, forwardCanvasRef, onCheckClick
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const borderImgRef = useRef<HTMLImageElement>(null);
  const emblemImgRef = useRef<HTMLImageElement>(null);
  const redrawRef = useRef({});
  const checkedCount = useRef(0);
  const [isAnimating, setAnimating] = useState<boolean>(false);
  const waveAnimPercentRef = useRef(0);

  const lf = (designLength) => {
    return designLength * width / 2322;
  }

  const [topSectionHeight, setTopSectionHeight] = useState(lf(115));
  const [midSectionHeight, setMidSectionHeight] = useState(lf(100));
  const emblemHeight = lf(260); // lf(280);
  const [checkboxesHeight, setCheckboxesHeight] = useState(lf(160)); // 260
  const [checkRects, setCheckRects] = useState<DOMRect[]>([]);


  const triggerAnimation = useThrottle(() => {
    setAnimating(true);
  }, 5000);

  const createCanvas = (): [CanvasRenderingContext2D, HTMLCanvasElement] => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    return [ctx, canvas];
  }

  useEffect(() => {
    if (!registerBlobGetter) {
      return;
    }
    /*
    Old cert was 2322 x 1642 which becomes 1200 x 848  (0.7)
    Facebook wants 1200 x 630 (0.525)
    BlueSky presents 1130 x 592 in their preview (0.525)
    */
    registerBlobGetter(() => {
      return new Promise((resolve, reject) => {
        const canvas = canvasRef.current;
        if (!canvas) return reject('No canvas');
        const scaledCanvas = document.createElement('canvas');
        scaledCanvas.width = 1200;
        scaledCanvas.height = 630;
        const ctx = scaledCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, scaledCanvas.width, scaledCanvas.height);
        scaledCanvas.toBlob((blob) => {
          if (!blob) reject('Blob failed');
          else resolve(blob);
        }, 'image/jpeg');
      });
    });
  }, [registerBlobGetter]);


  useEffect(() => {
    let isCancelled = false;

    async function loadImages() {
      const imageSources = type === CertificateType.GOLD
        ? ["/w_gold_base.png", "/gold_emblem.png"]
        : type === CertificateType.SILVER
        ? ["/w_silver_base.png", "/silver_emblem.png"]
        : type === CertificateType.BRONZE
        ? ["/w_bronze_base.png", "/bronze_emblem.png"]
        : ["/w_green_base.png", "/green_emblem.png"];

      try {
        const loadedImages = await Promise.all(
          imageSources.map((source) => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.src = source;
              img.onload = () => resolve(img);
              img.onerror = reject;
            });
          })
        );

        if (!isCancelled) {
          borderImgRef.current = loadedImages[0] as HTMLImageElement;
          emblemImgRef.current = loadedImages[1] as HTMLImageElement;
          drawCertificate(loadedImages[0], loadedImages[1] as HTMLImageElement);
          const newCheckedCount = checks.reduce((a, c) => a + (c.checked ? 1 : 0), 0);
          if (newCheckedCount !== checkedCount.current) {
            if (newCheckedCount > checkedCount.current) {
              triggerAnimation();
            }
            checkedCount.current = newCheckedCount;
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load images', error);
        }
      }
    }

    loadImages();

    console.log("effect: ", type, width, height, scale, topSectionHeight, midSectionHeight, checkboxesHeight, header, subheader, checks);
    return () => {
      isCancelled = true;
    };
  }, [type, width, height, scale, topSectionHeight, midSectionHeight, checkboxesHeight, header, subheader, checks]);


  useEffect(() => {
    if (!isAnimating) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frameId;
    let prevTimestamp = 0;
    waveAnimPercentRef.current = 0;

    const draw = (timestamp) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const timestampDiff = timestamp - prevTimestamp;
      const percentDiff = Math.min(0.25, 25 * timestampDiff / 1000);
      waveAnimPercentRef.current = Math.min(100, waveAnimPercentRef.current + percentDiff);
      redraw(waveAnimPercentRef.current);
      prevTimestamp = timestamp;

      if (waveAnimPercentRef.current < 100) {
        frameId = requestAnimationFrame(draw);
      } else {
        setAnimating(false);
      }
    };

    frameId = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(frameId);
  }, [isAnimating]);

  const drawCertificate = (borderImg, emblemImg: HTMLImageElement) => {


    const shinyHslByType = {
      [CertificateType.GOLD]: [37, 91, 27],
      [CertificateType.SILVER]: [180, 4, 27],
      [CertificateType.BRONZE]: [20, 40, 20],
      [CertificateType.GREEN]: [120, 80, 18],
    }

    const matteHslByType = {
      [CertificateType.GOLD]: [37, 45, 77],
      [CertificateType.SILVER]: [180, 2, 87],
      [CertificateType.BRONZE]: [20, 20, 74],
      [CertificateType.GREEN]: [120, 40, 72],
    }

    const backgroundFill = type === CertificateType.GOLD
      ? 'hsl(37, 30%, 13%)' // 'hsl(37, 30%, 12%)' // '#D9C98C'
      : type === CertificateType.SILVER
      ? 'hsl(180, 4%, 17%)' // '#B6B4AF'
      : type === CertificateType.BRONZE
      ? 'hsl(20, 15%, 13%)' // '#BBA395' // '#D2B693' // '#D2BD93'
      : 'hsl(122, 30%, 13%)' // 'hsl(125, 30%, 18%)' // '#b7d7b3'; //'#9FC69F';

    const emblemOffset = type === CertificateType.GREEN
      ? lf(-40)
      : type === CertificateType.BRONZE
      ? lf(-40)
      : type === CertificateType.SILVER
      ? lf(-20)
      : 0;

    const borderMargin = lf(175); // lf(197);

    const shsl = shinyHslByType[type];
    const shue = shsl[0];
    const ssat = shsl[1];
    const slig = shsl[2];

    const mhsl = matteHslByType[type];
    const mhue = mhsl[0];
    const msat = mhsl[1];
    const mlig = mhsl[2];
  
    const checkTextLength = checks.reduce((a, v) => a + v.text.length, 0);

    // Vertical layout
    const bottomSectionHeightish = Math.max(0.5 * (emblemHeight + checkboxesHeight), checkboxesHeight);
    const availableContentHeight = height - 2 * borderMargin;
    const botGapWeight = checkTextLength < 300 ? 0.7 : 0.8;
    const gapWeights = [1.0, 0.8, botGapWeight, botGapWeight];
    const vgapWeightSum = gapWeights.reduce((a, g) => a + g, 0);
    const vgap = (availableContentHeight - topSectionHeight - midSectionHeight - bottomSectionHeightish) / vgapWeightSum;
    // Gap between sections, but also before and after the sections, 2 top gaps have reduced weight
    const topSectionTop = borderMargin + gapWeights[0] * vgap;
    const midSectionTop = topSectionTop + topSectionHeight + gapWeights[1] * vgap;
    const bottomSectionMid = midSectionTop + midSectionHeight + gapWeights[2] * vgap + 0.5 * bottomSectionHeightish;

    // Canvases
    const canvas = canvasRef.current!;
    canvas.width = width;
    canvas.height = height;
    const [sctx, shinyCanvas] = createCanvas();
    const [mctx, matteCanvas] = createCanvas();

    // Metallic elevated offscreen canvas with border and shiny content
    sctx.clearRect(0, 0, width, height);
    sctx.drawImage(borderImg, 0, 0, width, height);
    // Matte offscreen canvas with background and other content
    mctx.fillStyle = backgroundFill;
    mctx.fillRect(0, 0, width, height); 

    // Emblem
    const eh = emblemHeight; // Math.max(bottomSectionHeightish, emblemHeight);
    const ew = eh * emblemImg.width / emblemImg.height;
    //sctx.fillStyle = mctx.fillStyle;
    //sctx.fillRect(0.5 * (width - ew), bottomSectionTop + emblemOffset * eh / emblemImg.height, ew, eh);
    sctx.drawImage(emblemImg, 0.5 * (width - ew), bottomSectionMid - 0.5 * eh + emblemOffset * eh / emblemImg.height, ew, eh);
    const titleBox = drawText(sctx, 120, header, topSectionTop, shue, ssat, slig, false, true, 0.5 * width, 0.8 * width, Align.CENTER, true, true);
    setTopSectionHeight(Math.round(titleBox.bottom - titleBox.top));

    const [subtitleBox] = drawMultilineText(mctx, 45, subheader, midSectionTop, mhue, msat, mlig, false, false, 0.5 * width, 0.6 * width, Align.CENTER, 1.3, true);
    setMidSectionHeight((prev) => {
      const newHeight = Math.round(subtitleBox.bottom - subtitleBox.top);
      const diff = Math.abs(newHeight - prev);
      return diff > 2 ? newHeight : prev;
    });

    /*
    mctx.strokeStyle = 'hsl(37, 91%, 14%)';
    mctx.lineWidth = 3;
    mctx.strokeRect(borderMargin, borderMargin, width - 2 * borderMargin, height - 2 * borderMargin);
    mctx.strokeRect(leftColumnLeft, borderMargin + 5, leftColumnWidth, height - 10 - 2 * borderMargin);
    mctx.strokeRect(rightColumnLeft, borderMargin + 5, rightColumnWidth, height - 10 - 2 * borderMargin);
    */
    const checkboxGap = lf(20);
    const checkFontSize = checkTextLength < 200
      ? 40
      : checkTextLength < 300
      ? 38
      : checkTextLength < 400
      ? 36
      : 34;
    const measureCheckboxes = (checks: Check[], w) => {
      var maxWidth = 0;
      const rects = [];
      checks.forEach((check) => {
        var bb: DOMRect = undefined;
        var prevText = check.text;
        while (true) {
          const bbAndLineCount = drawCheckbox(mctx, sctx, checkFontSize, check.checked, check.text, 0, 0, w, mhue, msat, mlig, true);
          bb = bbAndLineCount[0];
          if (bbAndLineCount[1] > check.maxLineCount) {
            if (check.adjustableText) {
              check.adjustableText.adjustDown();
            } else {
              check.plainText = check.plainText.substring(0, check.text.length - 6) + "…";
            }
          } else {
            break;
          }
          if (check.text === prevText) {
            break;
          }
          prevText = check.text;
        }
        maxWidth = Math.max(maxWidth, bb.width);
        rects.push(bb);
      });
      // Find the split that gives the lowest max column height
      const lowest = {
        split: 0,
        maxHeight: Number.MAX_SAFE_INTEGER,
        widths: [0, 0],
        heights: [0, 0]
      }
      const sumHeightReducer = (a, v: DOMRect) => a + v.height + (a > 0 ? checkboxGap : 0);
      for (let i = 1; i < checks.length; i++) {
        const l = rects.slice(0, i);
        const r = rects.slice(i);
        const maxHeight = Math.max(
          l.reduce(sumHeightReducer, 0),
          r.reduce(sumHeightReducer, 0)
        );
        if (maxHeight < lowest.maxHeight) {
          lowest.split = i;
          lowest.maxHeight = maxHeight;
          const maxWidthReducer = (a, v: DOMRect) => Math.max(a, v.width);
          lowest.widths = [ l.reduce(maxWidthReducer, 0), r.reduce(maxWidthReducer, 0) ];
          lowest.heights = [ l.reduce(sumHeightReducer, 0), r.reduce(sumHeightReducer, 0) ];
        }
      }
      // Should return two columns and [width, height] for each
      return [
        {
          checks: checks.slice(0, lowest.split),
          width: lowest.widths[0],
          height: lowest.heights[0]
        },
        {
          checks: checks.slice(lowest.split),
          width: lowest.widths[1],
          height: lowest.heights[1]
        },
      ];
    }
    const rects = []; // It's not good to find rects this way, but whatever
    const drawCheckboxes = (checkboxesTop, checks: Check[], x, w) => {
      var top = checkboxesTop;
      var maxWidth = 0;
      checks.forEach((check, index) => {
        const [bb] = drawCheckbox(mctx, sctx, checkFontSize, check.checked, check.text, x, top, w, mhue, msat, mlig, false);
        top += bb.height + (index < checks.length - 1 ? checkboxGap : 0);
        maxWidth = Math.max(maxWidth, bb.width);
        rects.push(bb);
      });
      return [maxWidth, top - checkboxesTop];
    }
    const availableWidth = 0.5 * (width - ew - 2 * borderMargin);
    const colw = (checkTextLength < 300 ? 0.8 : 0.85) * availableWidth;
    // Dry run to figure out split and column dimens
    const colDimens = measureCheckboxes(checks, colw);
    setCheckboxesHeight(Math.round([
      drawCheckboxes(bottomSectionMid - 0.5 * colDimens[0].height, colDimens[0].checks, borderMargin + 0.5 * (availableWidth - colDimens[0].width), colw),
      drawCheckboxes(bottomSectionMid - 0.5 * colDimens[1].height, colDimens[1].checks, 0.5 * (width + ew + availableWidth - colDimens[1].width), colw)
    ].reduce((max, dimen) => Math.max(max, dimen[1]), 0)));
    setCheckRects(rects);

    // Alpha mask
    const [actx, imageAlphaCanvas] = createCanvas();
    actx.drawImage(shinyCanvas, 0, 0, width, height);
    
    // Offscreen canvas: draw the gradient
    const [gctx, gradientCanvas] = createCanvas();

    const [mgctx, matteWithGradientCanvas] = createCanvas();

    redrawRef.current = {
      imageAlpha: [actx, imageAlphaCanvas],
      gradient: [gctx, gradientCanvas],
      shiny: [sctx, shinyCanvas],
      matte: [mctx, matteCanvas],
      matteWithGradient: [mgctx, matteWithGradientCanvas],
    }
    redraw(0);
  }

  const redraw = (waveAnimPercent) => {
    const [_actx, imageAlphaCanvas] = redrawRef.current["imageAlpha"];
    const [gctx, gradientCanvas] = redrawRef.current["gradient"];
    const [_sctx, shinyCanvas] = redrawRef.current["shiny"];
    const [_mctx, matteCanvas] = redrawRef.current["matte"];
    const [mgctx, matteWithGradientCanvas] = redrawRef.current["matteWithGradient"];
    const ctx = canvasRef.current.getContext('2d')!;

    const gradientLength = 0.8 * Math.sqrt(width * width + height * height);
    const gradientAngle = 65 * Math.PI / 180;
    const gradient = gctx.createLinearGradient(0, 0, gradientLength * Math.cos(gradientAngle), gradientLength * Math.sin(gradientAngle));
    const wavelength = 1.0 / 2.25;
    const offset = 0.10 + (1 * waveAnimPercent / 100);
    for (let p = 0.0; p <= 1.0; p += 1.0 / 15) {
      const fx = 0.5 * (1 - Math.cos(2 * p * Math.PI / wavelength - 2 * offset * Math.PI));
      if (type === CertificateType.BRONZE) {
        gradient.addColorStop(p, `rgba(255, 240, 216, ${0.33 * fx + 0.1})`);
      } else if (type === CertificateType.GREEN) {
        gradient.addColorStop(p, `rgba(255, 255, 255, ${0.50 * fx + 0.08})`);
      } else if (type === CertificateType.SILVER) {
        gradient.addColorStop(p, `rgba(245, 255, 255, ${0.42 * fx + 0.15})`);
      } else {
        gradient.addColorStop(p, `rgba(255, 255, 255, ${0.38 * fx + 0.1})`);
      }
    }
    gctx.clearRect(0, 0, width, height);
    gctx.fillStyle = gradient;
    gctx.fillRect(0, 0, width, height);


    // Main canvas: draw image (we'll use it as mask later)
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(shinyCanvas, 0, 0, width, height);


    // Gradient with blend modes
    ctx.globalCompositeOperation = 'overlay';
    ctx.drawImage(gradientCanvas, 0, 0);
    ctx.globalCompositeOperation = 'lighter';
    ctx.drawImage(gradientCanvas, 0, 0);


    // Weaker gradient on matte content
    mgctx.clearRect(0, 0, width, height);
    mgctx.drawImage(matteCanvas, 0, 0);
    mgctx.save();
    mgctx.globalCompositeOperation = 'overlay';
    mgctx.globalAlpha = type === CertificateType.SILVER ? 0.40 : 0.20;
    mgctx.drawImage(gradientCanvas, 0, 0);
    mgctx.restore();


    // Apply alpha mask using destination-in
    ctx.globalCompositeOperation = 'destination-in';            
    ctx.drawImage(imageAlphaCanvas, 0, 0);


    // Drop shadow
    ctx.globalCompositeOperation = 'destination-over';
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.30)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;
    ctx.drawImage(shinyCanvas, 0, 0, width, height);
    ctx.restore();

    // Fill under matte content
    ctx.drawImage(matteWithGradientCanvas, 0, 0, width, height);
  };

  return <CanvasContainer>
    <canvas
      ref={(el) => {
        canvasRef.current = el;
        if (forwardCanvasRef) {
          forwardCanvasRef.current = el;
        }
      }} 
      width={width} height={height} style={{
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      //backgroundColor: '#D9C98C'
    }}/>
    {onCheckClick && checkRects.map((r, index) => {
      return (<OverlayButton key={index} style={{
          top: `${r.y * scale}px`,
          left: `${r.x * scale}px`,
          width: `${r.width * scale}px`,
          height: `${r.height * scale}px`,
        }}
        onClick={() => {
          console.log(`left click index: ${index} ${!!onCheckClick} ${checks[index].checkKey}`);
          if (onCheckClick && checks[index].checkKey) {
            onCheckClick(checks[index].checkKey);
          }
        }}
      />);
    })}
  </CanvasContainer>;
};

const CanvasContainer = styled.div`
  display: block;
`

const OverlayButton = styled.div`
  position: absolute;
  background-color: rgba(64, 64, 64, 0.0);
  cursor: pointer;
`